import type {
  ApplySuggestedChangesValidationData,
  DiffEntry,
  SuggestedChange,
  SuggestedChangeLineRange,
  SuggestedChangesConfiguration,
  Thread,
} from '../conversation-types'

/**
 * Returns functions related to suggested changes
 */

/**
 * Determines if two line ranges intersect
 * Based on https://stackoverflow.com/a/35754308
 */
export function lineRangesIntersect(range1: SuggestedChangeLineRange, range2: SuggestedChangeLineRange): boolean {
  return range1.endLineNumber >= range2.startLineNumber && range1.startLineNumber <= range2.endLineNumber
}

/** Checks to see if a suggested change is in a pending batch of suggested changes */
export function isSuggestedChangeInPendingBatch(
  suggestedChange: SuggestedChange,
  pendingBatch: SuggestedChange[],
): boolean {
  return pendingBatch.some((batchedChange: SuggestedChange) => {
    return isSuggestedChangeEqual(batchedChange, suggestedChange)
  })
}

/** Determines whether a change is equivalent to another */
export function isSuggestedChangeEqual(a: SuggestedChange, b: SuggestedChange) {
  return (
    a.commentId === b.commentId &&
    a.path === b.path &&
    a.suggestion.length === b.suggestion.length &&
    suggestionsAreEqual(a.suggestion, b.suggestion)
  )
}

/** Checks to see if 2 suggestions are equal to each other */
const suggestionsAreEqual = (batchedSuggestion: string[], newSuggestion: string[]) => {
  if (batchedSuggestion.length !== newSuggestion.length) return false
  return !batchedSuggestion.some((sug, idx) => sug !== newSuggestion[idx])
}

// Arbitrary number we've chosen, can be tweaked based on performance
export const MAX_SUGGESTED_CHANGES_BATCH_SIZE = 50

/**
 * Minimal suggestion data needed for batch validation
 */
export type SuggestionForBatchValidation = {
  threadId?: number | null
  /** Path/line-range pairs for overlap detection */
  pathLineRanges?: Array<{path: string; lineRange: SuggestedChangeLineRange}>
}

/**
 * Collects all path/lineRange pairs from a suggestion.
 */
function getPathLineRanges(
  suggestion: SuggestionForBatchValidation,
): Array<{path: string; lineRange: SuggestedChangeLineRange}> {
  return suggestion.pathLineRanges ?? []
}

/**
 * Checks whether the suggested change lines intersect with any of the pending suggested changes in the batch
 * @param suggestedChange - the suggested change to check
 * @returns boolean
 */
function suggestedChangeLineRangesIntersect(
  suggestedChange: SuggestionForBatchValidation,
  pendingSuggestedChangesBatch: SuggestionForBatchValidation[],
): boolean {
  const newRanges = getPathLineRanges(suggestedChange)
  if (newRanges.length === 0) return false

  for (const pendingChange of pendingSuggestedChangesBatch) {
    const pendingRanges = getPathLineRanges(pendingChange)
    for (const newRange of newRanges) {
      for (const pendingRange of pendingRanges) {
        if (newRange.path !== pendingRange.path) continue
        if (lineRangesIntersect(newRange.lineRange, pendingRange.lineRange)) return true
      }
    }
  }
  return false
}

export const SUGGESTED_CHANGES_ERRORS = {
  USER_CANNOT_APPLY_SUGGESTION: 'You do not have permission to apply this suggestion.',
  PULL_REQUEST_IS_CLOSED: 'This suggestion cannot be applied because the pull request is closed.',
  PULL_REQUEST_IS_IN_MERGE_QUEUE: 'This suggestion cannot be applied because the pull request is queued to merge.',
  IS_OUTDATED: 'This suggestion is outdated and cannot be applied.',
  IS_PENDING: 'This suggestion is part of a pending comment and cannot be applied.',
  IS_RESOLVED: 'This suggestion is part of a resolved comment and cannot be applied.',
  ON_COMMIT_RANGE: 'Unable to apply suggestions when viewing a specific commit range.',
  UNCHANGED_CODE: 'This suggestion is invalid because no changes were made to the code.',
  INVALID_RANGE: 'Unable to commit due to other pending changes affecting this line.',
  BATCH_TOO_BIG: 'Unable to commit due to too many changes in the batch.',
  DELETED_LINES: 'Unable to apply suggestions on deleted lines.',
  RANGE_NOT_PROVIDED: 'This suggestion does not have a valid line range.',
  UNABLE_TO_DETERMINE_VALIDITY: 'Unable to determine the validity of this suggestion.',
  HEAD_OID_MISMATCH:
    'The pull request has been updated since this batch was created. Please refresh the page and try again.',
}

/**
 * Validates a suggested change can be applied, independent of batch
 */
export function validateSuggestedChangeCanBeApplied({
  suggestedChange,
  hasCommitRange,
  isOutdated,
  isPending,
  isResolved,
  pullRequestIsClosed,
  pullRequestIsInMergeQueue,
  userCanApplySuggestion,
  applySuggestedChangesValidationData,
  suggestedChangesConfig,
}: {
  suggestedChange: SuggestedChange
  hasCommitRange: boolean
  isOutdated: boolean | undefined
  isPending: boolean
  isResolved: boolean
  pullRequestIsClosed: boolean
  pullRequestIsInMergeQueue: boolean
  userCanApplySuggestion: boolean
  applySuggestedChangesValidationData: ApplySuggestedChangesValidationData
  suggestedChangesConfig: SuggestedChangesConfiguration | undefined
}): {
  isValid: boolean
  reason?: string
} {
  const {lineRange} = applySuggestedChangesValidationData
  if (isOutdated) return {isValid: false, reason: SUGGESTED_CHANGES_ERRORS.IS_OUTDATED}
  if (isPending) return {isValid: false, reason: SUGGESTED_CHANGES_ERRORS.IS_PENDING}
  if (isResolved) return {isValid: false, reason: SUGGESTED_CHANGES_ERRORS.IS_RESOLVED}
  if (pullRequestIsClosed) return {isValid: false, reason: SUGGESTED_CHANGES_ERRORS.PULL_REQUEST_IS_CLOSED}
  if (pullRequestIsInMergeQueue)
    return {isValid: false, reason: SUGGESTED_CHANGES_ERRORS.PULL_REQUEST_IS_IN_MERGE_QUEUE}
  if (!userCanApplySuggestion) return {isValid: false, reason: SUGGESTED_CHANGES_ERRORS.USER_CANNOT_APPLY_SUGGESTION}
  if (!lineRange) return {isValid: false, reason: SUGGESTED_CHANGES_ERRORS.RANGE_NOT_PROVIDED}
  if (hasCommitRange) return {isValid: false, reason: SUGGESTED_CHANGES_ERRORS.ON_COMMIT_RANGE}
  if (!suggestedChangesConfig) return {isValid: false, reason: SUGGESTED_CHANGES_ERRORS.UNABLE_TO_DETERMINE_VALIDITY}

  const isValidSuggestionRange = suggestedChangesConfig?.isValidSuggestionRange
  if (!isValidSuggestionRange) return {isValid: false, reason: SUGGESTED_CHANGES_ERRORS.DELETED_LINES}

  const originalLinesContentFromSuggestedChangeConfig = suggestedChangesConfig?.sourceContentFromDiffLines
  const suggestedChangeText = suggestedChange.suggestion.join('\n')
  const isEmptyLineDeletion = suggestedChange.suggestion.length === 0

  if (!isEmptyLineDeletion && suggestedChangeText === originalLinesContentFromSuggestedChangeConfig)
    return {isValid: false, reason: SUGGESTED_CHANGES_ERRORS.UNCHANGED_CODE}

  return {isValid: true}
}

/**
 * Validates whether a suggested change can be added to a batch
 */
export function validateSuggestedChangeCanBeAddedToBatch(
  suggestedChange: SuggestionForBatchValidation,
  pendingSuggestedChangesBatch: SuggestionForBatchValidation[],
): {isValid: boolean; reason?: string} {
  const invalidRangeResult = {
    isValid: false,
    reason: SUGGESTED_CHANGES_ERRORS.INVALID_RANGE,
  }
  const invalidBatchSizeResult = {
    isValid: false,
    reason: SUGGESTED_CHANGES_ERRORS.BATCH_TOO_BIG,
  }
  const validSuggestedChangeResult = {isValid: true}

  if (pendingSuggestedChangesBatch.length >= MAX_SUGGESTED_CHANGES_BATCH_SIZE) return invalidBatchSizeResult

  if (
    suggestedChange.threadId &&
    pendingSuggestedChangesBatch.find(pendingChange => pendingChange.threadId === suggestedChange.threadId)
  )
    return invalidRangeResult
  if (suggestedChangeLineRangesIntersect(suggestedChange, pendingSuggestedChangesBatch)) return invalidRangeResult

  return validSuggestedChangeResult
}

/**
 * Extracts path and line range pairs from ARC suggestion diff entries.
 * Used to populate overlap-detection data when adding ARC suggestions to a batch.
 *
 * Lines within a single DiffEntry may not be contiguous (e.g. two separate hunks
 * in the same file). We split them into individual contiguous ranges so that overlap
 * detection does not incorrectly flag lines in the gap between hunks.
 *
 * Uses left-side (original) coordinates for stable overlap detection, matching
 * the backend's `create_sorted_diff_lines` approach. For deletions, `left` is
 * the original line being removed. For additions, `left` is null in frontend
 * diff lines, so we track the last seen `left` value from surrounding context/deletion
 * lines as the insertion anchor. This prevents false collisions when one entry's
 * insertions shift right-side numbering to match another entry's line numbers.
 */
export function getLineRangesFromDiffEntries(
  diffEntries: DiffEntry[],
): Array<{path: string; lineRange: SuggestedChangeLineRange}> {
  const result: Array<{path: string; lineRange: SuggestedChangeLineRange}> = []
  for (const entry of diffEntries) {
    // Walk through diff lines tracking the last seen left-side line number.
    // For additions (where left is null), use the last seen left value as the
    // insertion anchor — this mirrors the backend enumerator which carries the
    // left-side position forward through additions. Left-side line numbers can
    // come from either context or deletion lines.
    let lastLeft = 0
    let groupStart: number | null = null
    let groupEnd = 0
    const seen = new Set<number>()

    // The orientation fields (`startOrientation`/`endOrientation`) are hardcoded to
    // 'LEFT' because they are only required to satisfy the `SuggestedChangeLineRange`
    // type. Overlap detection (`lineRangesIntersect`) only compares line numbers and
    // ignores orientation, so the value has no effect on conflict detection.
    //
    // 'LEFT' was chosen because it better represents what is actually being checked, which
    // is that there are no collisions of anchor lines, which for additions are the line above
    // the addition (or 0 if it is the top of the file), and for deletions is the line number
    // of the deleted line itself.
    const orientation = 'LEFT'

    for (const line of entry.diffLines) {
      // Update last seen left line number from context or deletion lines
      if (line.left != null) {
        lastLeft = line.left
      }

      // Use left line numbers for stable overlap detection.
      // For additions, left is null, so use the last seen left value as the anchor.
      let lineNum: number | null = null
      if (line.type === 'DELETION' && line.left != null) {
        lineNum = line.left
      } else if (line.type === 'ADDITION') {
        lineNum = lastLeft
      }

      // Skip if no line number (e.g Context, injected context, or hunk) or
      // already seen (e.g. multiple additions anchored to the same line, or modified lines (deletion with an addition)).
      //
      // Example:
      //
      // ```
      // foo  #Context,  left=1,    not added to seen
      // -bar #Deletion, left=2,    added to seen
      // +baz #Addition, left=null, anchored to lastLeft=2, not added to seen
      // bing #Context,  left=3,    not added to seen
      // ```
      // In this case, the deletion and addition of `bar` would have the same left line number. We only want to track one of them to avoid false positives in overlap detection.
      if (lineNum == null || seen.has(lineNum)) continue
      seen.add(lineNum)

      if (groupStart == null) {
        // First modification line in this entry, start a new group
        groupStart = lineNum
      } else if (lineNum - groupEnd > 1) {
        // Gap detected — flush the current group and start a new one
        result.push({
          path: entry.path,
          lineRange: {
            startLineNumber: groupStart,
            endLineNumber: groupEnd,
            startOrientation: orientation,
            endOrientation: orientation,
          },
        })

        // Start a new group at the current line number
        groupStart = lineNum
      }
      // Update the end of the current group to the current line number
      groupEnd = lineNum
    }

    // Flush the last group
    if (groupStart != null) {
      result.push({
        path: entry.path,
        lineRange: {
          startLineNumber: groupStart,
          endLineNumber: groupEnd,
          startOrientation: orientation,
          endOrientation: orientation,
        },
      })
    }
  }
  return result
}

/**
 * Returns the line range the suggested change applies to
 */
export function generateSuggestedChangeLineRangeFromDiffThread(
  thread: Thread | undefined,
): SuggestedChangeLineRange | undefined {
  if (!thread || thread.subjectType?.toUpperCase() !== 'LINE') return undefined

  // support both for backwards compatability in the interim
  const positioning = thread.positioning ?? thread.subject

  if (!positioning) return undefined

  const startDiffSide = positioning.startDiffSide ?? positioning.endDiffSide

  if (!startDiffSide) {
    return undefined
  }

  if ('isOutdated' in positioning && positioning.isOutdated) {
    return undefined
  }

  if (!positioning.endLine || !positioning.endDiffSide) {
    return undefined
  }

  const startLine = positioning.startLine ?? positioning.endLine
  return {
    endLineNumber: positioning.endLine,
    endOrientation: positioning.endDiffSide,
    startLineNumber: startLine,
    startOrientation: startDiffSide || 'RIGHT',
  }
}
